import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Sparkles } from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import V2Hero from '../../components/v2/V2Hero';
import { PrimaryCTA, CTABlock } from '../../components/v2/CTAButton';
import { PILLARS } from './pillars.data';

/**
 * /guides landing page. One indexable card per hub. Gives the "Guides"
 * dropdown a proper home and consolidates topical authority signals for
 * Google. Every card links to a hub that itself scores high on schema
 * (BreadcrumbList + FAQPage) and is auto-linked from every matching spoke
 * post via the interlink sweep.
 */

// Overrides / additions on top of PILLARS. `/podcast-management` isn't a
// pillar hub, it's a dedicated SEO service page that acts as the same-level
// entry point, so we include it here with a matching card. Order in this
// array is the display order on the /guides grid.
const GUIDE_ORDER = [
  'podcast-launching',
  'podcast-management',
  'book-publishing',
  'podcast-production-uk',
  'authority-hub-uk',
  'content-repurposing-uk',
  'small-business-seo-uk',
];

const EXTRAS = {
  'podcast-launching': {
    slug: 'podcast-launching',
    to: '/podcast-launch',
    title: 'How to Launch a Podcast',
    blurb: 'Strategy, name, artwork, first five episodes, hosting and distribution, done for you in under 30 days.',
    heroImage: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=1200&q=80&auto=format&fit=crop',
    heroAlt: 'Podcast microphone on a desk, for the Audio & Co. podcast launch service',
  },
  'podcast-management': {
    slug: 'podcast-management',
    to: '/podcast-management',
    title: 'Podcast Management Services',
    blurb: 'Editing, publishing, show notes, transcripts and distribution for coaches, consultants and small business owners. Fixed weekly output, no hourly bill.',
    heroImage: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=1200&q=80&auto=format&fit=crop',
    heroAlt: 'Creator recording a podcast at a home desk with a microphone and laptop, for the Audio & Co. podcast management guide',
  },
};

function buildCards() {
  return GUIDE_ORDER.map((slug) => {
    if (EXTRAS[slug]) return EXTRAS[slug];
    const p = PILLARS.find((x) => x.slug === slug);
    if (!p) return null;
    // Top-level slugs (podcast-launching, book-publishing) live at /<slug>.
    // Legacy pillars live under /guides/<slug>.
    const topLevel = new Set(['podcast-launching', 'book-publishing']);
    return {
      slug: p.slug,
      to: topLevel.has(p.slug) ? `/${p.slug}` : `/guides/${p.slug}`,
      title: p.title.replace(/[.]$/, ''),
      blurb: p.subheading,
      heroImage: p.heroImage,
      heroAlt: p.heroAlt,
    };
  }).filter(Boolean);
}

export default function GuidesIndex() {
  const cards = buildCards();

  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Guides: Podcasts, Publishing, Websites & SEO | Audio & Co."
        description="Free in-depth guides to podcast management, launching a podcast, book publishing, business websites and SEO for UK coaches and authors."
        keywords={[
          'podcast guides', 'book publishing guide', 'small business seo guide',
          'business website guide', 'podcast website uk', 'content repurposing playbook', 'launch a podcast',
        ]}
        url="https://audioandco.com/guides"
      />

      {/* BreadcrumbList schema */}
      <script
        type="application/ld+json"
        data-schema="breadcrumb"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://audioandco.com/' },
              { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://audioandco.com/guides' },
            ],
          }),
        }}
      />

      {/* CollectionPage / ItemList schema so Google sees this as a hub index. */}
      <script
        type="application/ld+json"
        data-schema="collection"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Audio & Co. Guides Library',
            description: 'Plain-English guides on launching a podcast, running a podcast, self-publishing a book, building a business website (Authority Hub), content repurposing and small business SEO.',
            url: 'https://audioandco.com/guides',
            inLanguage: 'en-GB',
            mainEntity: {
              '@type': 'ItemList',
              itemListElement: cards.map((c, i) => ({
                '@type': 'ListItem',
                position: i + 1,
                url: `https://audioandco.com${c.to}`,
                name: c.title,
              })),
            },
          }),
        }}
      />

      <V2Hero
        imageUrl="https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1800&q=80&auto=format&fit=crop"
        alt="Home desk with warm coffee, a notebook and headphones, for free Audio & Co. guides to podcasts, publishing and SEO"
        title="Free Podcast, Publishing & SEO Guides | Audio & Co."
        testId="guides-index-hero"
      >
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Guides Library
          </p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            Everything we know, in plain English.
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            Sixteen years of what actually works for small business owners: launch a podcast, run a weekly show, self-publish a book, build a business website (our Authority Hub model) and get found by Google. Pick your starting point.
          </p>
          <div className="mt-8">
            <CTABlock size="lg" testIdPrefix="guides-index-cta" />
          </div>
        </div>
      </V2Hero>

      <Section bg="bg" testId="guides-index-grid">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((c) => (
            <Link
              key={c.slug}
              to={c.to}
              className="group bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_4px_16px_rgba(30,42,68,0.04)] hover:shadow-[0_20px_50px_rgba(30,42,68,0.12)] hover:-translate-y-0.5 transition-all"
              data-testid={`guide-card-${c.slug}`}
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#F6F8FB]">
                <img
                  src={c.heroImage}
                  alt={c.heroAlt || c.title}
                  title={`${c.title} | Audio & Co.`}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-[#BC516F] font-semibold text-xs uppercase tracking-widest">
                  <BookOpen className="w-3.5 h-3.5" /> Guide
                </div>
                <h2 className="font-display font-semibold text-[#1E2A44] text-lg leading-snug mt-3">{c.title}</h2>
                <p className="text-[#64748B] text-[14px] leading-relaxed mt-2 line-clamp-4">{c.blurb}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#1E2A44] group-hover:text-[#BC516F] transition">
                  Read the guide <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">Reading is easy. Publishing weekly is what wins.</h2>
          <p className="text-white/75 mt-4 text-lg">
            The Pro Plan turns one recording into 12 published outputs, every week. 4-week minimum. Cancel any time. Free 30-min call.
          </p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix="guides-index-final-cta" />
          </div>
        </div>
      </Section>
    </main>
  );
}
